import React, { useState } from 'react';
import { nb } from 'date-fns/locale';
import { CalendarDays, ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatDateLong, isoToLocalDate, localDateToIso } from '@/domain/time';
import { cn } from '@/lib/utils';

export const FIELD = 'h-11 rounded-[10px] border-[var(--hr-border)] bg-white text-[15px] shadow-none transition-[border-color,box-shadow] duration-150 placeholder:text-[var(--hr-muted-2)] hover:border-[rgba(31,33,48,0.28)] focus-visible:border-[var(--hr-focus)] focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]/20 focus-visible:ring-offset-0 aria-[invalid=true]:border-[var(--hr-red)]';

export function Field({ id, label, hint, error, optional = false, children, className }) {
  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-baseline justify-between gap-2">
        <Label htmlFor={id} className="text-[13px] font-medium text-[var(--hr-text)]">
          {label}
        </Label>
        {optional && <span className="text-[12px] text-[var(--hr-muted-2)]">Valgfritt</span>}
      </div>
      {children}
      {error ? (
        <p className="text-[12.5px] text-[var(--hr-red)]" role="alert" data-testid={`${id}-error`}>
          {error}
        </p>
      ) : hint ? (
        <p className="text-[12.5px] text-[var(--hr-muted)]">{hint}</p>
      ) : null}
    </div>
  );
}

export function TextField({ id, label, value, onChange, error, hint, optional, type = 'text', placeholder, autoComplete, inputMode, testId, className, multiline = false, rows = 4 }) {
  const common = { id, value, onChange: (e) => onChange(e.target.value), placeholder, autoComplete, 'aria-invalid': !!error, 'data-testid': testId ?? id };
  return (
    <Field id={id} label={label} error={error} hint={hint} optional={optional} className={className}>
      {multiline ? <Textarea rows={rows} className={cn(FIELD, 'h-auto min-h-[112px] py-3 leading-6')} {...common} /> : <Input type={type} inputMode={inputMode} className={FIELD} {...common} />}
    </Field>
  );
}

/** Datovelger (nb-NO) med kalender i popover. Verdi er ISO yyyy-mm-dd eller null. */
export function DatePicker({ id, label, value, onChange, error, hint, optional, minDate, testId }) {
  const [open, setOpen] = useState(false);
  const today = new Date();
  return (
    <Field id={id} label={label} error={error} hint={hint} optional={optional}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" id={id} aria-invalid={!!error} className={cn(FIELD, 'flex w-full items-center gap-2 px-3 text-left', !value && 'text-[var(--hr-muted-2)]')} data-testid={testId ?? id}>
            <CalendarDays className="h-4 w-4 shrink-0 text-[var(--hr-muted)]" aria-hidden="true" />
            <span className="flex-1 truncate">{value ? formatDateLong(value) : 'Velg dato'}</span>
            <ChevronDown className="h-4 w-4 shrink-0 text-[var(--hr-muted-2)]" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto rounded-[12px] p-0 shadow-[var(--hr-shadow-md)]">
          <Calendar
            mode="single"
            locale={nb}
            weekStartsOn={1}
            selected={value ? isoToLocalDate(value) : undefined}
            defaultMonth={value ? isoToLocalDate(value) : today}
            fromDate={minDate ? isoToLocalDate(minDate) : today}
            onSelect={(d) => {
              onChange(d ? localDateToIso(d) : null);
              setOpen(false);
            }}
            initialFocus
          />
          {value && (
            <div className="border-t border-[var(--hr-hairline)] px-3 py-2 text-right">
              <button type="button" className="hr-link text-[12.5px]" onClick={() => { onChange(null); setOpen(false); }}>
                Fjern dato
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </Field>
  );
}

/** Kontaktfelt som deles av bestilling og prosjektforespørsel. */
export function CustomerFields({ value, onChange, errors = {}, prefix = 'customer' }) {
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid gap-5 sm:grid-cols-2">
      <TextField id={`${prefix}-name`} label="Navn" value={value.name} onChange={set('name')} error={errors.name} autoComplete="name" placeholder="Fornavn Etternavn" />
      <TextField id={`${prefix}-company`} label="Firma" optional value={value.company} onChange={set('company')} error={errors.company} autoComplete="organization" placeholder="Firmanavn AS" />
      <TextField id={`${prefix}-email`} label="E-post" type="email" value={value.email} onChange={set('email')} error={errors.email} autoComplete="email" inputMode="email" placeholder="navn@firma.no" />
      <TextField id={`${prefix}-phone`} label="Telefon" type="tel" value={value.phone} onChange={set('phone')} error={errors.phone} autoComplete="tel" inputMode="tel" placeholder="+47 900 00 000" />
      <TextField id={`${prefix}-orgNumber`} label="Organisasjonsnummer" optional value={value.orgNumber} onChange={set('orgNumber')} error={errors.orgNumber} inputMode="numeric" placeholder="9 siffer" className="sm:col-span-2" />
    </div>
  );
}

export function validateCustomer(c) {
  const e = {};
  if (!c.name || c.name.trim().length < 2) e.name = 'Skriv inn navn.';
  if (!c.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email)) e.email = 'Skriv inn en gyldig e-postadresse.';
  if (!c.phone || c.phone.replace(/\D/g, '').length < 8) e.phone = 'Skriv inn et telefonnummer.';
  if (c.orgNumber && c.orgNumber.replace(/\s/g, '').length !== 9) e.orgNumber = 'Organisasjonsnummer har 9 siffer.';
  return e;
}

export const EMPTY_CUSTOMER = { name: '', company: '', email: '', phone: '', orgNumber: '' };
